import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { SkeletonCenteredForm } from "../components/skeleton/Skeleton";
import { getLoggedInUser, getToken, logout, setAuthData } from "../helpers/authHelper";
import { normalizeColors, normalizeGoals } from "../helpers/onboardingData";
import { showError } from "../components/ToastNotifications";
import ProductDetailsEditor, { normalizeProducts, productsForSave, validateProducts } from "../components/businessProfile/ProductDetailsEditor";
import "./Auth.css";
import "./BusinessOnboarding.css";

const initialProfile = {
  brand_name: "",
  brand_description: "",
  industry: "",
  location: "",
  website_url: "",
  target_audience: "",
  customer_pain_points: "",
  products_services: "",
  differentiators: "",
  brand_tone: "",
  content_goals: [],
  colors: [],
  products: []
};

const goals = [
  ["brand_awareness", "Build brand awareness", "ti-speakerphone"],
  ["engagement", "Grow engagement", "ti-heart-handshake"],
  ["leads", "Generate leads", "ti-target-arrow"],
  ["sales", "Increase sales", "ti-shopping-bag"],
  ["website_traffic", "Drive website traffic", "ti-world-www"],
  ["conversions", "Increase conversions", "ti-chart-arrows-vertical"],
  ["education", "Educate customers", "ti-school"],
  ["community", "Build community", "ti-users-group"],
  ["other", "Other", "ti-dots"]
];

const tones = [
  ["Friendly", "Friendly and approachable", "ti-mood-smile", "Hey there! Here's a quick tip we think you'll love."],
  ["Professional", "Professional and clear", "ti-briefcase", "Three practical steps to streamline your week."],
  ["Playful", "Playful and energetic", "ti-confetti", "Plot twist: your to-do list just got shorter."],
  ["Authoritative", "Confident and authoritative", "ti-shield-check", "Here is what the data says, and what to do next."],
  ["Warm", "Warm and personal", "ti-heart", "We started this for people like you. Thank you."],
  ["Educational", "Helpful and educational", "ti-bulb", "New to this? Let's break it down in two minutes."]
];

const suggestedColors = ["#087e75", "#173b3a", "#ea785c", "#f2b84b", "#3b6fd8", "#7a5af8"];
const MAX_GOALS = 4;
const MAX_COLORS = 7;

const steps = [
  { label: "Business", title: "Business basics", description: "What do you do, and where can customers find you?", icon: "ti-building-store", tip: "Your name, industry and description anchor every post we write for you." },
  { label: "Audience", title: "Your customers", description: "Describe the people you serve and what they need from you.", icon: "ti-users", tip: "Specific audiences get specific content. Mention roles, needs or experience level." },
  { label: "Offer", title: "Your offer", description: "Give enough detail to make future content specific, not generic.", icon: "ti-package", tip: "List your main products or services and add a clear photo of each. AI images and videos use these photos to show your real products." },
  { label: "Voice & goals", title: "Voice and content goals", description: "Set the tone and the outcomes your content should support.", icon: "ti-microphone-2", tip: "Pick up to four goals. We balance your calendar across them." },
  { label: "Plan", title: "Choose your plan", description: "Start free or pick a plan with more capacity.", icon: "ti-rocket", tip: "You can switch plans at any time from Plans & billing." }
];
const PROFILE_STEPS = 4;

// Blocking rules (required, format, limits) plus non-blocking guidance (`recommend` = helpful minimum length).
const fieldRules = {
  brand_name: { label: "Business name", required: true, min: 2, max: 120 },
  industry: { label: "Industry", required: true, min: 2, max: 120 },
  brand_description: { label: "Business description", required: true, max: 2000, recommend: 60 },
  website_url: { label: "Website", url: true, max: 255 },
  location: { label: "Location", max: 160 },
  target_audience: { label: "Target audience", required: true, max: 2000, recommend: 40 },
  customer_pain_points: { label: "Customer needs and challenges", required: true, max: 2000, recommend: 40 },
  products_services: { label: "Products or services", required: true, max: 2500, recommend: 40 },
  differentiators: { label: "What makes the business distinct", required: true, max: 2000, recommend: 40 },
  brand_tone: { label: "Brand voice", required: true },
  content_goals: { label: "Content goals", required: true, maxItems: MAX_GOALS },
  colors: { label: "Brand colors", hex: true }
};

const stepFields = {
  1: ["brand_name", "industry", "brand_description", "website_url", "location"],
  2: ["target_audience", "customer_pain_points"],
  3: ["products_services", "differentiators"],
  4: ["brand_tone", "content_goals", "colors"]
};

const checkField = (field, value) => {
  const rule = fieldRules[field];
  if (Array.isArray(value)) {
    if (rule.required && value.length === 0) return { status: "error", message: `${rule.label} is required.` };
    if (rule.maxItems && value.length > rule.maxItems) return { status: "error", message: `Choose no more than ${rule.maxItems} content goals.` };
    if (rule.hex && value.some((color) => !/^#[\da-f]{6}$/i.test(color))) return { status: "error", message: "Use valid six-digit hex colors." };
    return { status: value.length ? "valid" : "empty", message: "" };
  }
  const text = String(value || "").trim();
  if (!text) return rule.required ? { status: "error", message: `${rule.label} is required.` } : { status: "empty", message: "" };
  if (rule.min && text.length < rule.min) return { status: "error", message: `${rule.label} needs at least ${rule.min} characters.` };
  if (rule.max && text.length > rule.max) return { status: "error", message: `${rule.label} must be ${rule.max} characters or fewer.` };
  if (rule.url) {
    try {
      const website = new URL(text);
      if (!["http:", "https:"].includes(website.protocol) || !website.hostname.includes(".")) throw new Error();
    } catch {
      return { status: "error", message: "Enter a complete website URL beginning with https://." };
    }
  }
  if (rule.recommend && text.length < rule.recommend) {
    return { status: "warning", message: `Looks good. About ${rule.recommend - text.length} more characters of detail will make your content sharper.` };
  }
  return { status: "valid", message: "" };
};

// 0–100 score for the rail: required answers count most, helpful detail adds the rest.
const profileStrength = (profile) => {
  let score = 0;
  let total = 0;
  for (const [field, rule] of Object.entries(fieldRules)) {
    const weight = rule.required ? 10 : 4;
    total += weight + (rule.recommend ? 4 : 0);
    const { status } = checkField(field, profile[field]);
    if (status === "valid" || status === "warning") score += weight;
    if (rule.recommend && status === "valid") score += 4;
  }
  return Math.round((score / total) * 100);
};

const strengthLabel = (value) => value >= 90 ? "Excellent" : value >= 70 ? "Strong" : value >= 40 ? "Good start" : "Getting started";

const money = (amount, currency = "INR") => new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format((Number(amount) || 0) / 100);

const planFeatureValue = (definition, plan) => {
  const value = plan.entitlements?.[definition.key];
  if (value === null || value === undefined) return null;
  if (definition.type === "boolean") return value ? definition.label : null;
  return `${new Intl.NumberFormat("en-IN").format(value)} ${definition.unit} · ${definition.label}`;
};

const feedbackIcon = (status) => status === "error" ? "ti-alert-triangle" : status === "warning" ? "ti-sparkles" : "ti-info-circle";

// Label, control, counter and animated feedback for one text field.
const Field = ({ id, label, optional, hint, check, visible, count, limit, children }) => {
  const status = visible ? check.status : "idle";
  const message = visible && check.message ? check.message : hint;
  return <div className={`ob-field is-${status}`}>
    <div className="ob-field-head">
      <label htmlFor={id}>{label}{optional && <span className="ob-optional">Optional</span>}</label>
      {limit && <span className={`ob-counter${count / limit >= 0.9 ? " is-near" : ""}`}>{count}/{limit}</span>}
    </div>
    <div className="ob-control">
      {children}
      <span className="ob-status-icon" aria-hidden="true"><i className={`ti ${status === "error" ? "ti-alert-circle" : status === "warning" ? "ti-bulb" : "ti-circle-check-filled"}`} /></span>
    </div>
    <div className="ob-feedback" id={`${id}-feedback`} aria-live="polite">
      {message && <span key={`${status}-${message}`} className={`ob-feedback-text${visible && check.message ? "" : " is-hint"}`}>
        {visible && check.message && <i className={`ti ${feedbackIcon(status)}`} aria-hidden="true" />}{message}
      </span>}
    </div>
  </div>;
};

// Final onboarding step. Prices come from the server's plan catalog; paid plans continue to the billing page,
// which runs checkout through the active payment gateway.
const OnboardingPlanStep = ({ onBack, onChoose }) => {
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [selected, setSelected] = useState("");
  const [leaving, setLeaving] = useState(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const result = await apiRequest("/api/public/plans");
      const plans = Array.isArray(result.plans) ? result.plans : [];
      setCatalog({ plans, featureDefinitions: result.featureDefinitions || [] });
      setSelected((current) => current || plans.find((plan) => plan.recommended)?.slug || plans[0]?.slug || "");
    } catch (requestError) {
      setLoadError(requestError.message || "Unable to load pricing plans.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const chosen = catalog?.plans.find((plan) => plan.slug === selected);
  const isFree = (plan) => plan.monthlyAmount === 0 && plan.annualAmount === 0;
  // The free plan is chosen explicitly too; nobody reaches the workspace without picking a plan.
  const freePlan = catalog?.plans.find(isFree);
  const choose = async (plan) => {
    setLeaving(true);
    const saved = await onChoose(plan ? { plan: plan.slug, interval: billingInterval, paid: !isFree(plan) } : { plan: "solo", interval: "monthly", paid: false });
    if (!saved) setLeaving(false);
  };

  if (loadError) return <div className="ob-plan-state" role="alert">
    <i className="ti ti-cloud-off" aria-hidden="true" />
    <p>{loadError}</p>
    <div className="ob-actions">
      <button type="button" className="ob-button is-ghost" onClick={() => choose(null)} disabled={leaving}>Start on the free plan</button>
      <button type="button" className="ob-button is-primary" onClick={load}><i className="ti ti-refresh" aria-hidden="true" />Try again</button>
    </div>
  </div>;
  if (!catalog) return <div className="ob-plans" aria-busy="true" role="status" aria-label="Loading plans">{[0, 1, 2].map((index) => <div className="ob-plan is-skeleton" key={index} />)}</div>;

  return <>
    <div className="ob-plan-toolbar">
      <div className="ob-saved-badge"><i className="ti ti-circle-check-filled" aria-hidden="true" />Business profile saved</div>
      <div className="ob-segmented" role="group" aria-label="Billing interval">
        <span className={`ob-segmented-thumb${billingInterval === "annual" ? " is-right" : ""}`} aria-hidden="true" />
        {["monthly", "annual"].map((value) => <button key={value} type="button" className={billingInterval === value ? "is-active" : ""} aria-pressed={billingInterval === value} onClick={() => setBillingInterval(value)}>{value === "monthly" ? "Monthly" : "Annual"}</button>)}
      </div>
    </div>
    {catalog.plans.length === 0 ? <p className="ob-muted">No plans are available right now. You can continue on the free plan.</p> : <div className="ob-plans" role="radiogroup" aria-label="Subscription plans">
      {catalog.plans.map((plan, index) => {
        const amount = billingInterval === "annual" ? plan.annualAmount : plan.monthlyAmount;
        const saving = plan.monthlyAmount * 12 - plan.annualAmount;
        const highlights = [
          ...catalog.featureDefinitions.map((definition) => planFeatureValue(definition, plan)).filter(Boolean),
          ...plan.features
        ].slice(0, 6);
        return <label key={plan.slug} style={{ "--ob-delay": `${index * 60}ms` }} className={`ob-plan${selected === plan.slug ? " is-selected" : ""}${plan.recommended ? " is-recommended" : ""}`}>
          <input type="radio" name="onboarding-plan" value={plan.slug} checked={selected === plan.slug} onChange={() => setSelected(plan.slug)} />
          <span className="ob-plan-check" aria-hidden="true"><i className="ti ti-check" /></span>
          {plan.recommended && <span className="ob-plan-badge"><i className="ti ti-star-filled" aria-hidden="true" />Recommended</span>}
          <strong className="ob-plan-name">{plan.name}</strong>
          <span className="ob-plan-description">{plan.description}</span>
          <span className="ob-plan-price-row">
            <span key={billingInterval} className="ob-plan-price">{amount === 0 ? "Free" : money(amount, plan.currency)}</span>
            {amount > 0 && <small> / {billingInterval === "annual" ? "year" : "month"}</small>}
          </span>
          <span className={`ob-plan-saving${billingInterval === "annual" && saving > 0 ? " is-visible" : ""}`} aria-hidden={!(billingInterval === "annual" && saving > 0)}>{saving > 0 ? `Save ${money(saving, plan.currency)} a year` : " "}</span>
          {highlights.length > 0 && <ul>{highlights.map((item) => <li key={item}><i className="ti ti-check" aria-hidden="true" />{item}</li>)}</ul>}
        </label>;
      })}
    </div>}
    <div className="ob-actions">
      <button type="button" className="ob-button is-ghost" onClick={onBack} disabled={leaving}><i className="ti ti-arrow-left" aria-hidden="true" />Back</button>
      <div className="ob-actions-end">
        {chosen && !isFree(chosen) && freePlan && <button type="button" className="ob-button is-link" onClick={() => choose(freePlan)} disabled={leaving}>Start free for now</button>}
        <button type="button" className="ob-button is-primary" disabled={(!chosen && catalog.plans.length > 0) || leaving} onClick={() => choose(chosen || null)}>
          {!chosen || isFree(chosen) ? "Start with the free plan" : `Continue with ${chosen.name}`}
          <i className="ti ti-arrow-right ob-button-arrow" aria-hidden="true" />
        </button>
      </div>
    </div>
    <p className="ob-footnote"><i className="ti ti-lock" aria-hidden="true" />Taxes are shown at checkout. Paid plans activate only after your payment is confirmed.</p>
  </>;
};

const BusinessOnboarding = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(initialProfile);
  const [step, setStep] = useState(1);
  const [furthest, setFurthest] = useState(1);
  const [direction, setDirection] = useState("forward");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [shaking, setShaking] = useState(false);
  const [goalLimitHit, setGoalLimitHit] = useState(0);
  const [savedAt, setSavedAt] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [savedProductCount, setSavedProductCount] = useState(0);
  const headingRef = useRef(null);
  const settled = useRef(false);

  useEffect(() => {
    apiRequest("/api/brand-settings/onboarding")
      .then(({ profile: savedProfile, completedStep, profileComplete, onboardingComplete }) => {
        if (onboardingComplete) {
          setAuthData(getToken(), { ...getLoggedInUser(), onboardingComplete: true });
          navigate("/dashboard", { replace: true });
          return;
        }
        if (savedProfile) setProfile((current) => ({
          ...current,
          ...Object.fromEntries(Object.entries(savedProfile).map(([key, value]) => [key, value ?? current[key]])),
          content_goals: normalizeGoals(savedProfile.content_goals),
          colors: normalizeColors(savedProfile.colors),
          products: normalizeProducts(savedProfile.products)
        }));
        if (savedProfile) setSavedProductCount(normalizeProducts(savedProfile.products).length);
        // A finished profile without a chosen plan resumes at the plan step.
        const resumeAt = profileComplete || completedStep >= PROFILE_STEPS ? PROFILE_STEPS + 1 : Math.min((completedStep || 0) + 1, PROFILE_STEPS);
        setStep(resumeAt);
        setFurthest(resumeAt);
      })
      .catch((requestError) => showError(requestError.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  // After a step change, move focus to the new heading so keyboard and screen-reader users follow along.
  useEffect(() => {
    if (loading) return;
    if (!settled.current) { settled.current = true; return; }
    headingRef.current?.focus({ preventScroll: true });
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, loading]);

  const checks = useMemo(() => Object.fromEntries(Object.keys(fieldRules).map((field) => [field, checkField(field, profile[field])])), [profile]);
  const strength = useMemo(() => profileStrength(profile), [profile]);
  const isVisible = (field) => Boolean(touched[field] || submitted[step]);
  const currentErrors = (stepFields[step] || []).filter((field) => checks[field].status === "error");

  const setValue = (field, value) => setProfile((current) => ({ ...current, [field]: value }));
  const touch = (field) => setTouched((current) => current[field] ? current : { ...current, [field]: true });

  const inputProps = (field) => ({
    id: `ob-${field}`,
    name: field,
    value: profile[field],
    maxLength: fieldRules[field].max,
    onChange: (event) => setValue(field, event.target.value),
    onBlur: () => touch(field),
    "aria-invalid": isVisible(field) && checks[field].status === "error" ? true : undefined,
    "aria-describedby": `ob-${field}-feedback`,
    "aria-required": fieldRules[field].required ? true : undefined
  });

  const fieldProps = (field) => ({
    id: `ob-${field}`,
    check: checks[field],
    visible: isVisible(field),
    count: profile[field].length,
    limit: fieldRules[field].max >= 500 ? fieldRules[field].max : undefined
  });

  const normalizeWebsite = () => {
    touch("website_url");
    const value = profile.website_url.trim();
    if (value && !/^[a-z][a-z\d+.-]*:\/\//i.test(value)) setValue("website_url", `https://${value}`);
  };

  const toggleGoal = (goal) => {
    touch("content_goals");
    if (profile.content_goals.includes(goal)) {
      setValue("content_goals", profile.content_goals.filter((item) => item !== goal));
    } else if (profile.content_goals.length >= MAX_GOALS) {
      setGoalLimitHit((count) => count + 1);
    } else {
      setValue("content_goals", [...profile.content_goals, goal]);
    }
  };

  const addColor = (color = "#087e75") => {
    if (profile.colors.length < MAX_COLORS) setValue("colors", [...profile.colors, color]);
  };

  const goToStep = (target) => {
    if (target < 1 || target === step || target > furthest || saving) return;
    setDirection(target < step ? "back" : "forward");
    setStep(target);
  };

  const focusField = (field) => {
    const element = document.getElementById(`ob-${field}`) || document.querySelector(`[data-field="${field}"] input`);
    element?.focus();
  };

  const saveStep = async (event) => {
    event.preventDefault();
    setSubmitted((current) => ({ ...current, [step]: true }));
    if (currentErrors.length) {
      setShaking(true);
      const firstInvalid = currentErrors[0];
      window.requestAnimationFrame(() => focusField(firstInvalid));
      return;
    }
    if (step === 3) {
      const productError = uploadingImages ? "Wait for product images to finish uploading." : validateProducts(profile.products, { previousCount: savedProductCount });
      if (productError) {
        setShaking(true);
        showError(productError);
        return;
      }
    }

    setSaving(true);
    try {
      const result = await apiRequest(`/api/brand-settings/onboarding/step/${step}`, {
        method: "PUT",
        body: JSON.stringify({ ...profile, products: productsForSave(profile.products) })
      });
      if (step === PROFILE_STEPS && !(result.profileComplete ?? result.completedStep >= PROFILE_STEPS)) {
        throw new Error("Your profile was saved, but it is not marked complete. Please try again.");
      }
      setSavedAt(new Date());
      if (step === 3) setSavedProductCount(profile.products.length);
      setDirection("forward");
      setFurthest((current) => Math.max(current, step + 1));
      setStep(step + 1);
    } catch (requestError) {
      showError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  // Onboarding finishes only once the server has recorded the plan choice. A paid choice then opens its
  // checkout on the billing page; a free choice goes to the dashboard. Returns false if it could not be saved.
  const choosePlan = async (choice) => {
    try {
      await apiRequest("/api/brand-settings/onboarding/plan", {
        method: "PUT",
        body: JSON.stringify({ planSlug: choice.plan, interval: choice.interval })
      });
      setAuthData(getToken(), { ...getLoggedInUser(), onboardingComplete: true });
      navigate(choice.paid ? `/upgrade-plan?plan=${encodeURIComponent(choice.plan)}&interval=${choice.interval}` : "/dashboard", { replace: true });
      return true;
    } catch (requestError) {
      showError(requestError.message || "Unable to save your plan choice. Please try again.");
      return false;
    }
  };

  const signOut = () => {
    logout();
    navigate("/", { replace: true });
  };

  if (loading) return <SkeletonCenteredForm label="Loading your business profile" />;

  const current = steps[step - 1];
  const progress = (step - 1) / (steps.length - 1);

  return (
    <div className="ob">
      <header className="ob-topbar">
        <span className="ob-brand"><span><img className="ob-brand-logo" src={`${process.env.PUBLIC_URL}/assets/images/logo/socialyorix-logo.png`} alt="Socialyorix" /></span></span>
        <div className="ob-topbar-end">
          {savedAt && <span key={savedAt.getTime()} className="ob-autosave" role="status"><i className="ti ti-cloud-check" aria-hidden="true" />Saved</span>}
          <span className="ob-step-pill">STEP {step} OF {steps.length}</span>
          <button type="button" className="ob-button is-ghost is-small" onClick={signOut}><i className="ti ti-logout-2" aria-hidden="true" />Sign out</button>
        </div>
      </header>
      <div className="ob-progress" aria-hidden="true"><span style={{ transform: `scaleX(${Math.max(progress, 0.04)})` }} /></div>

      <div className="ob-layout">
        <aside className="ob-rail" aria-label="Onboarding progress">
          <ol className="ob-steps">
            {steps.map((item, index) => {
              const number = index + 1;
              const state = number === step ? "current" : number < furthest ? "done" : "upcoming";
              return <li key={item.label} className={`ob-step is-${state}`}>
                <button type="button" onClick={() => goToStep(number)} disabled={number > furthest || number === step || saving} aria-current={state === "current" ? "step" : undefined}>
                  <span className="ob-step-dot" aria-hidden="true"><i className={`ti ${state === "done" ? "ti-check" : item.icon}`} /></span>
                  <span className="ob-step-copy"><strong>{String(number).padStart(2, "0")} {item.label}</strong><small>{state === "done" ? "Completed" : state === "current" ? "In progress" : "Up next"}</small></span>
                </button>
              </li>;
            })}
          </ol>
          <div className="ob-strength">
            <div className="ob-strength-head"><span>Profile strength</span><strong>{strengthLabel(strength)}</strong></div>
            <div className="ob-strength-track" role="progressbar" aria-label="Profile strength" aria-valuemin={0} aria-valuemax={100} aria-valuenow={strength}>
              <span style={{ transform: `scaleX(${strength / 100})` }} className={strength >= 70 ? "is-strong" : strength >= 40 ? "is-good" : ""} />
            </div>
            <small>{strength}% · richer answers make sharper content</small>
          </div>
          <div className="ob-tip" key={step}><i className="ti ti-bulb" aria-hidden="true" /><p>{current.tip}</p></div>
        </aside>

        <main className="ob-main">
          <div key={step} className={`ob-stage is-${direction}`}>
            <div className="ob-stage-head">
              <span className="ob-stage-icon" aria-hidden="true"><i className={`ti ${current.icon}`} /></span>
              <div>
                {step > PROFILE_STEPS && <span className="ob-eyebrow">Your profile is ready</span>}
                <h1 ref={headingRef} tabIndex={-1}>{current.title}</h1>
                <p>{current.description}</p>
              </div>
            </div>

            {step > PROFILE_STEPS ? <div className="ob-card">
              <OnboardingPlanStep onBack={() => goToStep(PROFILE_STEPS)} onChoose={choosePlan} />
            </div> : <form className={`ob-card${shaking ? " is-shaking" : ""}`} onSubmit={saveStep} onAnimationEnd={(event) => { if (event.target === event.currentTarget) setShaking(false); }} noValidate>
              {submitted[step] && currentErrors.length > 0 && <div className="ob-summary" role="alert">
                <i className="ti ti-alert-octagon" aria-hidden="true" />
                <div>
                  <strong>{currentErrors.length === 1 ? "1 field needs attention" : `${currentErrors.length} fields need attention`}</strong>
                  <ul>{currentErrors.map((field) => <li key={field}><button type="button" onClick={() => focusField(field)}>{checks[field].message}</button></li>)}</ul>
                </div>
              </div>}

              {step === 1 && <div className="ob-grid">
                <Field label="Business or brand name" {...fieldProps("brand_name")}><input className="ob-input" autoComplete="organization" {...inputProps("brand_name")} /></Field>
                <Field label="Industry" hint="For example: specialty food, fitness, accounting" {...fieldProps("industry")}><input className="ob-input" placeholder="Your industry or niche" {...inputProps("industry")} /></Field>
                <div className="ob-span">
                  <Field label="What does your business do?" hint="Two or three sentences in your own words." {...fieldProps("brand_description")}><textarea className="ob-input" rows="4" {...inputProps("brand_description")} /></Field>
                </div>
                <Field label="Website" optional hint="We'll add https:// if you leave it out." {...fieldProps("website_url")}>
                  <input className="ob-input" type="url" inputMode="url" placeholder="example.com" autoComplete="url" {...inputProps("website_url")} onBlur={normalizeWebsite} />
                </Field>
                <Field label="Location" optional hint="City, region or service area" {...fieldProps("location")}><input className="ob-input" placeholder="e.g. Pune, Maharashtra" {...inputProps("location")} /></Field>
              </div>}

              {step === 2 && <div className="ob-grid is-single">
                <Field label="Who is your ideal customer?" hint="Include traits like needs, context, experience level or buying role." {...fieldProps("target_audience")}><textarea className="ob-input" rows="4" {...inputProps("target_audience")} /></Field>
                <Field label="What challenges or questions do they bring?" hint="This guides useful, empathetic content and DM replies." {...fieldProps("customer_pain_points")}><textarea className="ob-input" rows="4" placeholder="What are they trying to solve, improve or understand?" {...inputProps("customer_pain_points")} /></Field>
              </div>}

              {step === 3 && <div className="ob-grid is-single">
                <Field label="Products or services" hint="Your main offers, who each is for, and any useful details." {...fieldProps("products_services")}><textarea className="ob-input" rows="4" {...inputProps("products_services")} /></Field>
                <Field label="Why do customers choose you?" hint="Expertise, approach, quality, values or outcomes that set you apart." {...fieldProps("differentiators")}><textarea className="ob-input" rows="4" {...inputProps("differentiators")} /></Field>
                <ProductDetailsEditor
                  variant="onboarding"
                  inputClassName="ob-input"
                  products={profile.products}
                  onChange={(products) => setValue("products", products)}
                  onUploadingChange={setUploadingImages}
                  title={<>Product &amp; service details<span className="ob-optional">Optional</span></>}
                  description="Add up to 7 products or services. Product photos can be uploaded from your device or chosen from your media gallery; AI-generated images and videos use them to show your real products."
                />
              </div>}

              {step === 4 && <>
                <fieldset className={`ob-fieldset is-${isVisible("brand_tone") ? checks.brand_tone.status : "idle"}`} data-field="brand_tone">
                  <legend>How should your brand sound?</legend>
                  <div className="ob-tones" role="radiogroup" aria-label="Brand voice" aria-describedby="ob-brand_tone-feedback">
                    {tones.map(([value, label, icon, sample], index) => <label key={value} style={{ "--ob-delay": `${index * 40}ms` }} className={`ob-tone${profile.brand_tone === value ? " is-selected" : ""}`}>
                      <input type="radio" name="brand_tone" value={value} checked={profile.brand_tone === value} onChange={() => { setValue("brand_tone", value); touch("brand_tone"); }} />
                      <span className="ob-tone-icon" aria-hidden="true"><i className={`ti ${icon}`} /></span>
                      <span className="ob-tone-copy"><strong>{label}</strong><em>“{sample}”</em></span>
                    </label>)}
                  </div>
                  <div className="ob-feedback" id="ob-brand_tone-feedback" aria-live="polite">
                    {isVisible("brand_tone") && checks.brand_tone.message && <span className="ob-feedback-text"><i className="ti ti-alert-triangle" aria-hidden="true" />{checks.brand_tone.message}</span>}
                  </div>
                </fieldset>

                <fieldset className={`ob-fieldset is-${isVisible("content_goals") ? checks.content_goals.status : "idle"}`} data-field="content_goals">
                  <legend>
                    What should your content help achieve?
                    <span key={goalLimitHit} className={`ob-goal-count${goalLimitHit ? " is-bump" : ""}${profile.content_goals.length === MAX_GOALS ? " is-full" : ""}`}>{profile.content_goals.length}/{MAX_GOALS} selected</span>
                  </legend>
                  <div className="ob-chips" aria-describedby="ob-content_goals-feedback">
                    {goals.map(([value, label, icon]) => {
                      const checked = profile.content_goals.includes(value);
                      return <label key={value} className={`ob-chip${checked ? " is-checked" : ""}${!checked && profile.content_goals.length >= MAX_GOALS ? " is-dimmed" : ""}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleGoal(value)} />
                        <i className={`ti ${checked ? "ti-check" : icon}`} aria-hidden="true" />
                        <span>{label}</span>
                      </label>;
                    })}
                  </div>
                  <div className="ob-feedback" id="ob-content_goals-feedback" aria-live="polite">
                    {isVisible("content_goals") && checks.content_goals.message
                      ? <span className="ob-feedback-text"><i className="ti ti-alert-triangle" aria-hidden="true" />{checks.content_goals.message}</span>
                      : goalLimitHit > 0 && profile.content_goals.length >= MAX_GOALS
                        ? <span key={goalLimitHit} className="ob-feedback-text is-warning"><i className="ti ti-hand-stop" aria-hidden="true" />You can pick up to {MAX_GOALS}. Remove one to choose another.</span>
                        : <span className="ob-feedback-text is-hint">Choose 1 to {MAX_GOALS}.</span>}
                  </div>
                </fieldset>

                <fieldset className="ob-fieldset" data-field="colors">
                  <legend>Brand colors <span className="ob-optional">Optional · up to {MAX_COLORS}</span></legend>
                  <div className="ob-swatches">
                    {profile.colors.map((color, index) => <div className="ob-swatch" key={`${index}-${color}`}>
                      <input type="color" aria-label={`Brand color ${index + 1}`} value={color} onChange={(event) => setValue("colors", profile.colors.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} />
                      <code>{color.toUpperCase()}</code>
                      <button type="button" className="ob-swatch-remove" aria-label={`Remove brand color ${index + 1}`} onClick={() => setValue("colors", profile.colors.filter((_, itemIndex) => itemIndex !== index))}><i className="ti ti-x" aria-hidden="true" /></button>
                    </div>)}
                    {profile.colors.length < MAX_COLORS && <button className="ob-swatch-add" type="button" onClick={() => addColor()}><i className="ti ti-plus" aria-hidden="true" />Add color</button>}
                  </div>
                  {profile.colors.length < MAX_COLORS && <div className="ob-suggestions">
                    <span>Quick picks</span>
                    {suggestedColors.filter((color) => !profile.colors.includes(color)).map((color) => <button key={color} type="button" className="ob-suggestion" style={{ "--ob-swatch": color }} aria-label={`Add ${color}`} title={color} onClick={() => addColor(color)} />)}
                    {profile.colors.length > 0 && <button className="ob-button is-link is-small" type="button" onClick={() => setValue("colors", [])}>Clear all</button>}
                  </div>}
                </fieldset>
              </>}

              <div className="ob-actions">
                <button type="button" className="ob-button is-ghost" onClick={() => goToStep(step - 1)} disabled={step === 1 || saving}><i className="ti ti-arrow-left" aria-hidden="true" />Back</button>
                <button type="submit" className="ob-button is-primary" disabled={saving || uploadingImages}>
                  {saving && <span className="ob-spinner" aria-hidden="true" />}
                  {saving ? "Saving..." : step === PROFILE_STEPS ? "Save and choose a plan" : "Save and continue"}
                  {!saving && <i className="ti ti-arrow-right ob-button-arrow" aria-hidden="true" />}
                </button>
              </div>
              <p className="ob-footnote"><i className="ti ti-lock" aria-hidden="true" />Each step is saved securely as you continue.</p>
            </form>}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BusinessOnboarding;
